/**
 * React Query hooks for Model Availability management
 *
 * Provides data fetching and mutations for:
 * - Updating model availability (global/restricted)
 * - Managing tenant model access grants
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { providerKeys } from './usePlatformProviders';
import type {
  ModelAvailability,
  TenantModelAccess,
  TenantModelAccessCreate,
} from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const availabilityKeys = {
  all: ['model-availability'] as const,
  access: (modelId: string) => [...availabilityKeys.all, 'access', modelId] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetch tenant access grants for a specific model
 */
export function useModelAccess(modelId: string) {
  return useQuery({
    queryKey: availabilityKeys.access(modelId),
    queryFn: async () => {
      const response = await apiClient.get<{ data: TenantModelAccess[] }>(
        `/api/v1/admin/platform-models/${modelId}/access`
      );
      return response.data.data;
    },
    enabled: !!modelId,
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Update model availability (global/restricted)
 */
export function useUpdateModelAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      modelId,
      availability,
    }: {
      modelId: string;
      availability: ModelAvailability;
    }) => {
      const response = await apiClient.patch(
        `/api/v1/admin/platform-models/${modelId}/availability`,
        { availability }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: availabilityKeys.all });
    },
  });
}

/**
 * Grant a tenant access to a restricted model
 */
export function useGrantModelAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      modelId,
      data,
    }: {
      modelId: string;
      data: TenantModelAccessCreate;
    }) => {
      const response = await apiClient.post<{ data: TenantModelAccess }>(
        `/api/v1/admin/platform-models/${modelId}/access`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: availabilityKeys.access(variables.modelId),
      });
    },
  });
}

/**
 * Revoke a tenant's access to a restricted model
 */
export function useRevokeModelAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      modelId,
      tenantId,
    }: {
      modelId: string;
      tenantId: string;
    }) => {
      await apiClient.delete(
        `/api/v1/admin/platform-models/${modelId}/access/${tenantId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: availabilityKeys.access(variables.modelId),
      });
    },
  });
}
